/**
 * Per-region wall thickening (V7, phase 2).
 *
 * The user assigns extra wall thickness to points ON the trim curve (the gingival margin).
 * The wall, however, runs from the margin up over the crown, so the thickness must apply to
 * the whole vertical wall column at that arch position — gingiva → occlusal top. We model the
 * extra as a function of LATERAL position only (the plane ⟂ the occlusal axis), constant along
 * the occlusal axis: a 2D nearest-trim-point (Voronoi) field of thickness, lightly blurred so
 * thickened/normal sections transition smoothly instead of stepping. The wall band's OUTER iso
 * is then pushed out per voxel by the sampled extra; the inner face stays at the cement-gap
 * clearance (never grows into the tooth).
 */

import { Vector3 } from 'three';
import { VoxelGrid } from '@modules/three/common/voxels';

export type ThicknessField = {
  /** Smoothed per-node extra thickness (mm), row-major [j*cols + i]. */
  t: Float32Array;
  cols: number;
  rows: number;
  aMin: number;
  bMin: number;
  cell: number;
  u: Vector3;
  v: Vector3;
};

/** Orthonormal basis (u, v) spanning the plane ⟂ dir (matches alignerTrimCut). */
function planeBasis(dir: Vector3): { u: Vector3; v: Vector3 } {
  const ref = Math.abs(dir.x) < 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
  const u = new Vector3().crossVectors(ref, dir).normalize();
  const v = new Vector3().crossVectors(dir, u).normalize();
  return { u, v };
}

function clampInt(value: number, count: number): number {
  return value < 0 ? 0 : value >= count ? count - 1 : value;
}

/**
 * Build the lateral extra-thickness field from per-trim-point thickness values. Returns null
 * when there is nothing to thicken (all zero / too few points), so the caller can take the
 * cheap uniform-band path.
 */
export function buildLateralThicknessField(
  points: Vector3[],
  thickness: number[] | undefined,
  occlusalAxis: Vector3,
  cellSize: number,
): ThicknessField | null {
  const n = points.length;
  if (n < 3 || !thickness || thickness.length === 0) return null;
  let maxT = 0;
  for (let i = 0; i < Math.min(n, thickness.length); i++) maxT = Math.max(maxT, thickness[i] || 0);
  if (maxT <= 0) return null;

  const dir = occlusalAxis.clone().normalize();
  const { u, v } = planeBasis(dir);

  // Project trim points to lateral (a, b) + carry their thickness.
  const pa = new Float32Array(n);
  const pb = new Float32Array(n);
  const pt = new Float32Array(n);
  let aMin = Infinity;
  let aMax = -Infinity;
  let bMin = Infinity;
  let bMax = -Infinity;
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const a = p.x * u.x + p.y * u.y + p.z * u.z;
    const b = p.x * v.x + p.y * v.y + p.z * v.z;
    pa[i] = a;
    pb[i] = b;
    pt[i] = thickness[i] || 0;
    if (a < aMin) aMin = a;
    if (a > aMax) aMax = a;
    if (b < bMin) bMin = b;
    if (b > bMax) bMax = b;
  }

  // Lattice bounds: trim extent + margin so all wall voxels project inside (out-of-range
  // lookups clamp to the edge).
  const marginMm = 4;
  aMin -= marginMm;
  bMin -= marginMm;
  aMax += marginMm;
  bMax += marginMm;
  const cols = Math.max(2, Math.ceil((aMax - aMin) / cellSize) + 1);
  const rows = Math.max(2, Math.ceil((bMax - bMin) / cellSize) + 1);

  // Bucket grid over trim points for fast nearest queries.
  const binSize = Math.max(2, cellSize * 8);
  const binCols = Math.max(1, Math.ceil((aMax - aMin) / binSize));
  const binRows = Math.max(1, Math.ceil((bMax - bMin) / binSize));
  const bins: number[][] = Array.from({ length: binCols * binRows }, () => []);
  for (let i = 0; i < n; i++) {
    const bi = clampInt(Math.floor((pa[i] - aMin) / binSize), binCols);
    const bj = clampInt(Math.floor((pb[i] - bMin) / binSize), binRows);
    bins[bj * binCols + bi].push(i);
  }
  const maxRing = binCols + binRows;

  // Nearest-trim-point thickness per node (Voronoi).
  const t = new Float32Array(cols * rows);
  for (let j = 0; j < rows; j++) {
    const b = bMin + j * cellSize;
    const bj = clampInt(Math.floor((b - bMin) / binSize), binRows);
    for (let i = 0; i < cols; i++) {
      const a = aMin + i * cellSize;
      const bi = clampInt(Math.floor((a - aMin) / binSize), binCols);

      let best = Infinity;
      let bestT = 0;
      for (let r = 0; r <= maxRing; r++) {
        for (let dj = -r; dj <= r; dj++) {
          const cj = bj + dj;
          if (cj < 0 || cj >= binRows) continue;
          const onRowEdge = dj === -r || dj === r;
          for (let di = -r; di <= r; di++) {
            if (!onRowEdge && di !== -r && di !== r) continue; // ring only
            const ci = bi + di;
            if (ci < 0 || ci >= binCols) continue;
            const bucket = bins[cj * binCols + ci];
            for (let k = 0; k < bucket.length; k++) {
              const p = bucket[k];
              const da = a - pa[p];
              const db = b - pb[p];
              const d2 = da * da + db * db;
              if (d2 < best) {
                best = d2;
                bestT = pt[p];
              }
            }
          }
        }
        if (best < Infinity && r * binSize * (r * binSize) >= best) break;
      }
      t[j * cols + i] = bestT;
    }
  }

  blurField(t, cols, rows, Math.max(1, Math.round(1.5 / cellSize)));

  return { t, cols, rows, aMin, bMin, cell: cellSize, u, v };
}

/** Separable box blur (edge-clamped) so thickened→normal sections ramp smoothly. */
function blurField(t: Float32Array, cols: number, rows: number, radius: number): void {
  const tmp = new Float32Array(t.length);
  // Horizontal
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      let sum = 0;
      let cnt = 0;
      for (let d = -radius; d <= radius; d++) {
        const ii = clampInt(i + d, cols);
        sum += t[j * cols + ii];
        cnt++;
      }
      tmp[j * cols + i] = sum / cnt;
    }
  }
  // Vertical
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      let sum = 0;
      let cnt = 0;
      for (let d = -radius; d <= radius; d++) {
        const jj = clampInt(j + d, rows);
        sum += tmp[jj * cols + i];
        cnt++;
      }
      t[j * cols + i] = sum / cnt;
    }
  }
}

/** Bilinear sample of the extra thickness at lateral (a, b) (edge-clamped). */
function sampleField(field: ThicknessField, a: number, b: number): number {
  const fa = (a - field.aMin) / field.cell;
  const fb = (b - field.bMin) / field.cell;
  const i0 = clampInt(Math.floor(fa), field.cols);
  const j0 = clampInt(Math.floor(fb), field.rows);
  const i1 = clampInt(i0 + 1, field.cols);
  const j1 = clampInt(j0 + 1, field.rows);
  const ta = Math.min(1, Math.max(0, fa - i0));
  const tb = Math.min(1, Math.max(0, fb - j0));
  const t = field.t;
  const t00 = t[j0 * field.cols + i0];
  const t10 = t[j0 * field.cols + i1];
  const t01 = t[j1 * field.cols + i0];
  const t11 = t[j1 * field.cols + i1];
  const top = t00 + (t10 - t00) * ta;
  const bot = t01 + (t11 - t01) * ta;
  return top + (bot - top) * tb;
}

/**
 * Wall band with a per-voxel OUTER iso pushed out by the lateral thickness field. Negative
 * inside `{innerIso ≤ d ≤ outerIso + extra(a,b)}`; inner face stays at `innerIso`. The field
 * is 2D (lateral), so the extra is broadcast along the occlusal axis → the whole column
 * thickens, outward only.
 */
export function buildVariableWallBandGrid(
  grid: VoxelGrid,
  innerIso: number,
  outerIso: number,
  field: ThicknessField,
): VoxelGrid {
  const out = new VoxelGrid(grid.info);
  const { counts, origin, cellSize } = grid.info;
  const { u, v } = field;
  const data = grid.data;
  const dst = out.data;

  let idx = 0;
  for (let x = 0; x < counts.x; x++) {
    const wx = origin.x + x * cellSize;
    for (let y = 0; y < counts.y; y++) {
      const wy = origin.y + y * cellSize;
      for (let z = 0; z < counts.z; z++) {
        const wz = origin.z + z * cellSize;
        const value = data[idx];
        const a = wx * u.x + wy * u.y + wz * u.z;
        const b = wx * v.x + wy * v.y + wz * v.z;
        const extra = sampleField(field, a, b);
        // negative inside the wall band, positive on both sides
        dst[idx] = Math.max(innerIso - value, value - (outerIso + extra));
        idx++;
      }
    }
  }
  return out;
}
